import { activityRetentionLabel } from './activityRetention'
import { useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { EmptyState } from '@/components/ui/empty-state'
import { FilterDropdown } from '@/components/ui/filter-dropdown'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { displayDateTime } from '@/lib/dates'
import { words, type RecordError } from './detailTypes'
import { useConnectionHistory } from './useConnectionDetail'

function ErrorRow({ error }: { error: RecordError }) {
  return (
    <TableRow className={error.resolved_at ? 'text-muted-foreground' : undefined}>
      <TableCell>
        <Badge variant="neutral">{words(error.kind)}</Badge>
        {error.resolved_at && (
          <p className="text-caption">Resolved · {displayDateTime(error.resolved_at)}</p>
        )}
      </TableCell>
      <TableCell>
        {error.link ? (
          <a
            href={error.link}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline"
          >
            {error.source_type} {error.external_id}
            <ExternalLink className="size-3.5" strokeWidth={1.5} />
          </a>
        ) : (
          `${error.source_type} ${error.external_id}`
        )}
      </TableCell>
      <TableCell>{error.message}</TableCell>
      <TableCell>{displayDateTime(error.first_seen_at)}</TableCell>
      <TableCell>{displayDateTime(error.last_seen_at)}</TableCell>
      <TableCell className="text-right tabular-nums">{error.occurrences}</TableCell>
    </TableRow>
  )
}
export function ConnectionErrors({ id }: { id: number }) {
  const [status, setStatus] = useState<ReadonlySet<string>>(new Set(['open']))
  const [kind, setKind] = useState<ReadonlySet<string>>(new Set())
  const resolved = status.size === 1 ? String(status.has('resolved')) : 'all'
  const query = useConnectionHistory(id, 'record_errors', `?resolved=${resolved}`)
  const errors = query.data?.record_errors ?? []
  const filtered = errors.filter((error) => !kind.size || kind.has(error.kind))
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <FilterDropdown
          label="Kind"
          emptyLabel="Kind"
          options={Array.from(new Set(errors.map((error) => error.kind))).map((value) => ({
            value,
            label: words(value),
          }))}
          selected={kind}
          onChange={setKind}
        />
        <FilterDropdown
          label="Status"
          emptyLabel="Status"
          options={[
            { value: 'open', label: 'Open' },
            { value: 'resolved', label: 'Resolved' },
          ]}
          selected={status}
          onChange={setStatus}
        />
      </div>
      {query.isPending ? (
        <p>Loading errors…</p>
      ) : query.isError ? (
        <Alert>Could not load errors</Alert>
      ) : (
        <ErrorTable errors={filtered} />
      )}
    </div>
  )
}
function ErrorTable({ errors }: { errors: RecordError[] }) {
  if (!errors.length) return <EmptyState variant="quiet" title="No matching errors" />
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {['Kind', 'Record', 'Message', 'First seen', 'Last seen', 'Times'].map((name) => (
            <TableHead key={name}>{name}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {errors.map((error) => (
          <ErrorRow key={error.id} error={error} />
        ))}
      </TableBody>
    </Table>
  )
}
export function ConnectionActivity({ id }: { id: number }) {
  const query = useConnectionHistory(id, 'audits')
  if (query.isPending) return <p>Loading activity…</p>
  if (query.isError) return <Alert>Could not load activity</Alert>
  const entries = query.data.audits
  if (!entries.length) return <EmptyState variant="quiet" title="No activity yet" />
  return (
    <div className="max-w-3xl divide-y divide-border">
      {entries.map((entry) => (
        <div key={entry.id} className="py-2 text-body-sm">
          <span className="font-medium">{words(entry.event.replace(/^connection_/, ''))}</span>
          <span> · {entry.actor.name} · </span>
          <time className="text-muted-foreground" dateTime={entry.occurred_at}>
            {displayDateTime(entry.occurred_at)}
          </time>
          {Object.entries(entry.details).map(([key, value]) => (
            <p key={key} className="text-caption text-muted-foreground">
              {words(key)}:{' '}
              {entry.event === 'activity_retention_changed'
                ? activityRetentionLabel(value as number | null)
                : String(value)}
            </p>
          ))}
        </div>
      ))}
    </div>
  )
}
